import { useState } from "react";
import VideoCard from "@/components/feature/VideoCard";
import EmptyState from "@/components/feature/EmptyState";
import type { Video } from "@/types/video";
import { getEditorPicks, getHotVideos, getLatestVideos } from "@/utils/video";

const tabs = [
  { key: "latest", label: "最新上线", hint: "刚刚更新" },
  { key: "hot", label: "今日热门", hint: "大家都在看" },
  { key: "picks", label: "编辑精选", hint: "口碑优先" },
] as const;

type TabKey = (typeof tabs)[number]["key"];

const dataMap: Record<TabKey, () => Video[]> = {
  latest: () => getLatestVideos(8),
  hot: () => getHotVideos(8),
  picks: () => getEditorPicks(8),
};

export default function FeaturedTabs() {
  const [active, setActive] = useState<TabKey>("latest");
  const list = dataMap[active]();

  return (
    <section className="page-shell pb-12 md:pb-16">
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="mb-2 flex items-center gap-2 font-label text-[11px] uppercase tracking-[0.2em] text-primary-400">
            <span className="h-px w-6 bg-primary-500" />
            Curated for you
          </p>
          <h2 className="font-heading text-xl font-semibold tracking-tight text-foreground-950 md:text-2xl">
            精选视频推荐
          </h2>
          <p className="mt-1.5 text-sm text-foreground-600">
            最新的、最热的、口碑最好的，一次都摆在你面前。
          </p>
        </div>

        <div className="flex w-full items-center gap-1 rounded-full border border-background-200 bg-background-100 p-1 sm:w-auto">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setActive(tab.key)}
              className={`flex-1 whitespace-nowrap rounded-full px-3.5 py-1.5 font-label text-xs transition-colors sm:flex-none ${
                active === tab.key
                  ? "bg-primary-500 text-background-50"
                  : "text-foreground-600 hover:text-foreground-950"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {list.length === 0 ? (
        <EmptyState title="暂无推荐视频，敬请期待" description="我们正在整理新的内容，稍后再来看看。" />
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {list.map((video) => (
            <div key={video.id} className="animate-fade-in">
              <VideoCard video={video} />
            </div>
          ))}
        </div>
      )}

      <div className="mt-6 flex items-center gap-2 text-xs text-foreground-500">
        <i className="ri-information-line text-sm" />
        {tabs.find((tab) => tab.key === active)?.hint} · 数据每日更新
      </div>
    </section>
  );
}